// Device position for check-in / check-out. The backend rejects WEB
// check-ins without it. Browsers only allow this on HTTPS or localhost.

const OPTIONS = { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 };
const MAX_ACCURACY_M = 100000; // backend limit

const deniedMessage = (actionLabel) =>
  `Location is required to ${actionLabel}. Allow location access in your browser settings and try again.`;
const UNAVAILABLE_MESSAGE =
  "Couldn't get your location. Move to an open area or check that location services are on.";

// Marks location failures so callers can show them inline instead of as API errors.
//   denied: the browser (or the user) blocked location for this site
export class LocationError extends Error {
  constructor(message, { denied = false } = {}) {
    super(message);
    this.name = "LocationError";
    this.denied = denied;
  }
}

// Resolves to { latitude, longitude, accuracy }; rejects with a LocationError.
//   actionLabel: "check in" | "check out" (used in the permission message)
export const getDeviceLocation = (actionLabel = "check in") =>
  new Promise((resolve, reject) => {
    if (!window.isSecureContext || !navigator.geolocation) {
      reject(new LocationError(UNAVAILABLE_MESSAGE));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) =>
        resolve({
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracy: Math.min(coords.accuracy, MAX_ACCURACY_M),
        }),
      (error) => {
        const denied = error.code === error.PERMISSION_DENIED;
        reject(
          new LocationError(
            denied ? deniedMessage(actionLabel) : UNAVAILABLE_MESSAGE,
            { denied },
          ),
        );
      },
      OPTIONS,
    );
  });

// "granted" | "prompt" | "denied" | "unsupported" (no Permissions API, e.g. older Safari)
// | "insecure" (plain http: browsers block location entirely).
export const getLocationPermission = async () => {
  if (!window.isSecureContext) return "insecure";
  if (!navigator.geolocation) return "unsupported";
  try {
    const result = await navigator.permissions.query({ name: "geolocation" });
    return result.state;
  } catch {
    return "unsupported";
  }
};

// Straight-line distance in meters between two points (haversine).
// Informational only: the server decides whether a check-in is allowed.
export const distanceInMeters = (a, b) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLng = toRad(b.longitude - a.longitude);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.latitude)) *
      Math.cos(toRad(b.latitude)) *
      Math.sin(dLng / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.sqrt(h));
};

// 350 -> "350 m", 2630 -> "2.6 km"
export const formatDistance = (meters) => {
  if (meters == null || Number.isNaN(Number(meters))) return "—";
  const value = Number(meters);
  return value < 1000
    ? `${Math.round(value)} m`
    : `${(value / 1000).toFixed(1)} km`;
};
